/**
 * Accès filtré par rôle au Centre d'aide + helpers de navigation/recherche.
 *
 * Toute la documentation vit côté client (`content/`). Le rôle de l'utilisateur
 * (auth store) détermine ce qui est VISIBLE : on n'affiche ni section, ni
 * article, ni sous-article dont le `minRole` dépasse son rang.
 */
import { computed } from 'vue'
import { useAuthStore } from '@/shared/stores/auth'
import { docSections } from '../content'
import { ROLE_RANK, type DocArticle, type DocSection, type RoleId } from '../content/types'

function rank(role: RoleId | undefined): number {
  return ROLE_RANK[role ?? 'agent']
}

/** Filtre récursif d'un article selon le rang de l'utilisateur (hérite du parent). */
function filterArticle(a: DocArticle, userRank: number, inherited: RoleId | undefined): DocArticle | null {
  const min = a.minRole ?? inherited
  if (rank(min) > userRank) return null
  const children = (a.children ?? [])
    .map((c) => filterArticle(c, userRank, min))
    .filter((c): c is DocArticle => c !== null)
  return { ...a, children: children.length ? children : undefined }
}

export function useDocs() {
  const auth = useAuthStore()
  const userRank = computed(() => rank((auth.user?.role as RoleId) ?? 'agent'))

  /** Sections accessibles à l'utilisateur, articles filtrés en profondeur. */
  const sections = computed<DocSection[]>(() =>
    docSections
      .filter((s) => rank(s.minRole) <= userRank.value)
      .map((s) => ({
        ...s,
        articles: s.articles
          .map((a) => filterArticle(a, userRank.value, s.minRole))
          .filter((a): a is DocArticle => a !== null),
      }))
      .filter((s) => s.articles.length > 0),
  )

  function getSection(id: string): DocSection | undefined {
    return sections.value.find((s) => s.id === id)
  }

  /** Recherche un article (et sa chaîne de parents) par id dans une section. */
  function findArticle(
    section: DocSection,
    articleId: string,
  ): { article: DocArticle; trail: DocArticle[] } | undefined {
    const walk = (list: DocArticle[], trail: DocArticle[]): { article: DocArticle; trail: DocArticle[] } | undefined => {
      for (const a of list) {
        if (a.id === articleId) return { article: a, trail: [...trail, a] }
        if (a.children) {
          const hit = walk(a.children, [...trail, a])
          if (hit) return hit
        }
      }
      return undefined
    }
    return walk(section.articles, [])
  }

  /** Recherche plein-texte légère sur titres/résumés (sections accessibles). */
  function search(query: string): { sectionId: string; article: DocArticle }[] {
    const q = query.trim().toLowerCase()
    if (!q) return []
    const out: { sectionId: string; article: DocArticle }[] = []
    const walk = (sectionId: string, list: DocArticle[]) => {
      for (const a of list) {
        const hay = `${a.title} ${a.summary ?? ''}`.toLowerCase()
        if (hay.includes(q)) out.push({ sectionId, article: a })
        if (a.children) walk(sectionId, a.children)
      }
    }
    for (const s of sections.value) walk(s.id, s.articles)
    return out.slice(0, 20)
  }

  return { sections, getSection, findArticle, search, userRank }
}
